import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Coffee } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { CafeForm, type CafeFormRef } from "@/components/cafe-form";
import { setActiveContextId } from "@/hooks/use-app-context";

export const Route = createFileRoute("/_authenticated/negocios/nuevo-cafe")({
  head: () => ({
    meta: [
      { title: "Registra tu café — Brulo" },
      { name: "description", content: "Registra tu café en Brulo y conéctalo con la comunidad corredora." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: NuevoCafePage,
});

function NuevoCafePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const formRef = useRef<CafeFormRef>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      const result = await formRef.current?.submit();
      if (!result?.data) {
        const first = result?.errors ? Object.values(result.errors)[0] : null;
        toast.error(first ?? "Revisa los campos del formulario");
        return;
      }
      const d = result.data;
      const hours =
        d.opens_at && d.closes_at ? `${d.opens_at} - ${d.closes_at}` : null;

      const { data: cafe, error } = await supabase
        .from("cafes")
        .insert({
          owner_id: user.id,
          name: d.name.trim(),
          slug: "",
          location: d.location,
          city: d.city.trim(),
          estado: d.estado.trim() || null,
          phone: d.phone.trim() || null,
          opens_at: d.opens_at || null,
          closes_at: d.closes_at || null,
          hours,
          google_maps_url: d.google_maps_url.trim() || null,
          description: d.description.trim() || null,
          member_discount: d.member_discount.trim() || null,
          calle: d.calle.trim() || null,
          numero_exterior: d.numero_exterior.trim() || null,
          colonia: d.colonia.trim() || null,
          codigo_postal: d.codigo_postal.trim() || null,
          latitude: d.latitude ? Number(d.latitude) : null,
          longitude: d.longitude ? Number(d.longitude) : null,
        })
        .select("id")
        .single();

      if (error || !cafe) throw error ?? new Error("No se pudo crear el café");

      const { error: linkError } = await supabase
        .from("cafe_managers")
        .insert({ cafe_id: cafe.id, user_id: user.id, role: "owner" });
      if (linkError) throw linkError;

      await queryClient.invalidateQueries({ queryKey: ["app-contexts", user.id] });
      setActiveContextId(cafe.id);
      toast.success(
        "Tu café fue registrado y está pendiente de aprobación. Te avisaremos cuando esté publicado.",
      );
      navigate({ to: "/cafe/dashboard" });
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "No se pudo registrar tu café. Inténtalo de nuevo.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Volver
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <span className="rounded-full bg-primary/10 p-2 text-primary">
            <Coffee className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold">Registra tu café</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          Cuéntanos dónde está tu café. Tu registro quedará pendiente de aprobación
          antes de publicarse.
        </p>

        <div className="rounded-xl border border-border/60 bg-card/50 p-5 space-y-5">
          <CafeForm ref={formRef} />
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="w-full min-h-11 rounded-md bg-primary px-4 py-3 font-semibold text-primary-foreground hover:bg-primary/90 transition disabled:opacity-60"
          >
            {saving ? "Registrando…" : "Registrar café"}
          </button>
        </div>
      </div>
    </div>
  );
}
